import { ProjectTotalization, RealtimeTippingComment } from "interfaces/front/business";
import { BaseClientService, ErrorV2, Result } from "../BaseClientService";
import { Aniale, DonationCourse, RewardCourse } from "interfaces/front/page/project";

export class FrontProjectService extends BaseClientService {
  url: string;
  cachedUrl: string;

  constructor() {
    super();
    this.url = this.API_BASE_URL + "/api/front/projects";
    this.cachedUrl = this.API_CF_BASE_URL + "/api/front/projects";
  }

  async getCourses(code: string): Promise<Result<GetCoursesOutput, ErrorV2>> {
    const url = `${this.cachedUrl}/${code}/courses`;
    const response = await fetch(url, {
      credentials: "include",
      method: "GET",
      headers: this.headers,
    });
    return this.request<GetCoursesOutput, ErrorV2>(response);
  }

  async getSummary(code: string): Promise<Result<ProjectTotalization, ErrorV2>> {
    const url = `${this.url}/${code}/summary`;
    const response = await fetch(url, {
      credentials: "include",
      method: "GET",
      headers: this.headers,
    });
    return this.request<ProjectTotalization, ErrorV2>(response);
  }

  async getSupportComments(
    code: string,
    newestCreatedAt: string,
    onSuccess: (supportComments: SupportComment[]) => void,
    onError: () => void
  ): Promise<void> {
    const url = `${this.cachedUrl}/${code}/support_comments?newest_created_at=${newestCreatedAt}`;
    const response = await fetch(url, {
      credentials: "include",
      method: "GET",
      headers: this.headers,
    });
    if (response.ok) {
      const supportComments = (await response.json()) as SupportComment[];
      onSuccess(supportComments);
    } else {
      await onError();
    }
  }

  async getRealtimeTippingComment(code: string): Promise<Result<RealtimeTippingCommentOutput, ErrorV2>> {
    return super.doGet<RealtimeTippingCommentOutput, ErrorV2>(this.cachedUrl, `${code}/tipping_comments/realtime`);
  }

  async getMonumentNameInfos(code: string, onSuccess: (monumentNameInfos: MonumentNameInfo[]) => void, onError: () => void): Promise<void> {
    const url = `${this.url}/${code}/monument_names`;
    const response = await fetch(url, {
      credentials: "include",
      method: "GET",
      headers: this.headers,
    });
    if (response.ok) {
      onSuccess((await response.json()) as MonumentNameInfo[]);
    } else {
      await onError();
    }
  }

  async getLottery(code: string, onSuccess: (lottery: Lottery) => void, onError: () => void): Promise<void> {
    const url = `${this.url}/${code}/current_lottery`;
    const response = await fetch(url, {
      credentials: "include",
      method: "GET",
      headers: this.headers,
    });
    if (response.ok) {
      onSuccess((await response.json()) as Lottery);
    } else {
      await onError();
    }
  }
}

export type RealtimeTippingCommentOutput = {
  realtime_comment: RealtimeTippingComment | null;
  next_interval: number;
};

interface GetCoursesOutput {
  reward_courses: RewardCourse[];
  donation_course: DonationCourse | null;
  aniale: Aniale | null;
}

export interface SupportComment {
  id: number;
  commenter_nickname: string;
  commenter_avatar_url: string | null;
  comment: string;
  created_at: string;
}

export interface MonumentNameInfo {
  id: string;
  monument_name: string;
}

export interface Lottery {
  subject: string;
  image_url: string;
  choice1: string;
  choice2: string;
  choice3: string;
  tweet_image_url: string;
  answer_count: number;
  answer_count_all: number;
}
